import { releasesHoldOnCancel } from '@/kiosk-core/payment/activeIntent';
import type { PersistedActiveIntent } from '@/kiosk-core/payment/terminalPayment';
import type { components, operations } from '@/services/api/v1.gen';

type Schemas = components['schemas'];

export interface CreatePaymentIntentRequestInput {
  amount: number;
  currency: string;
  type: 'terminal';
  idempotencyKey: string;
  sessionId: string;
  partnerStripeId: string | null;
  // Order identity. Without it the backend cannot tell a second purchase from
  // a retry, and refuses every purchase after the first on this session.
  checkoutKey?: string;
  copyAmountsCents?: readonly number[];
}

export interface PaymentIntentReaderRequestInput {
  paymentIntentId: string;
  readerId: string;
  sessionId: string;
  partnerStripeId: string | null;
}

export interface PaymentIntentStatusRequestInput {
  paymentIntentId: string;
  sessionId: string;
  partnerStripeId: string | null;
}

export type PaymentIntentStatusQuery = NonNullable<
  operations['payment_intents_get_payment_intent']['parameters']['query']
>;

// The payment transport and the unload beacon use the same backend. The
// static container of ENTR does not serve /api.
export function paymentApiBaseUrl(): string {
  return (
    import.meta.env.VITE_ENTR_PAYMENT_API_BASE_URL ??
    import.meta.env.VITE_KIOSK_API_BASE_URL ??
    ''
  );
}

export function cancelReaderUrl(): string {
  return `${paymentApiBaseUrl().replace(/\/+$/, '')}/api/v1/payment-intents/cancel-reader`;
}

export function buildCreatePaymentIntentBody(
  input: CreatePaymentIntentRequestInput
): Schemas['CreatePaymentIntentPayload'] {
  return {
    amount: input.amount,
    // Kiosk currency is a free-form config string; the backend enum validates it.
    currency: input.currency as Schemas['Currency'],
    type: input.type,
    idempotency_key: input.idempotencyKey,
    session_id: input.sessionId,
    partner_stripe_id: input.partnerStripeId,
    ...(input.checkoutKey ? { checkout_key: input.checkoutKey } : {}),
    // The backend refuses one field without the other.
    ...(input.copyAmountsCents?.length
      ? {
          expected_copies: input.copyAmountsCents.length,
          copy_amounts_cents: [...input.copyAmountsCents],
        }
      : {}),
  };
}

export function buildSendToReaderBody(
  input: Pick<PaymentIntentReaderRequestInput, 'readerId' | 'partnerStripeId'>
): Schemas['SendPaymentIntentToReaderPayload'] {
  return {
    reader_id: input.readerId,
    partner_stripe_id: input.partnerStripeId,
    simulate_reader: false,
  };
}

export function buildPaymentIntentStatusQuery(
  input: PaymentIntentStatusRequestInput & { readerId?: string }
): PaymentIntentStatusQuery {
  return {
    partner_stripe_id: input.partnerStripeId,
    ...(input.readerId ? { reader_id: input.readerId } : {}),
  };
}

// Capture alone carries the order identity. The capture guard compares it
// with the order that the session last paid for. The hold step uses the same
// body and the same guard.
export function buildCapturePaymentIntentBody(input: {
  partnerStripeId: string | null;
  checkoutKey?: string;
}): Schemas['CapturePaymentIntentPayload'] {
  return {
    partner_stripe_id: input.partnerStripeId,
    ...(input.checkoutKey ? { checkout_key: input.checkoutKey } : {}),
  };
}

// The session id and the intent id let the backend find the Stripe account in
// its database. Without them, it tries each connected account.
//
// A cancel at the reader stage ends a payment that the kiosk did not accept
// and will not accept. Thus the backend can release an amount that the reader
// held in the last second. A reset of the reader after the capture stage
// releases nothing (see releasesHoldOnCancel), unless the kiosk gives up a
// hold that the hold step did not accept. The backend never releases a hold
// that the kiosk accepted.
export function buildCancelPaymentOnReaderBody(
  intent: Pick<
    PersistedActiveIntent,
    'paymentIntentId' | 'readerId' | 'sessionId' | 'partnerStripeId' | 'capture'
  > &
    Partial<Pick<PersistedActiveIntent, 'stage'>>,
  releaseHold: boolean = releasesHoldOnCancel(intent)
): Schemas['CancelReaderActionPayload'] {
  return {
    payment_intent_id: intent.paymentIntentId,
    reader_id: intent.readerId,
    session_id: intent.sessionId,
    partner_stripe_id: intent.partnerStripeId ?? null,
    ...(releaseHold ? { release_hold: true } : {}),
  };
}
